import axios from 'axios'
import { taxExemptCertPreviewSource } from './portalTaxExemptCerts.js'
import {
  appendInstructorPortalOrderSource,
  instructorPortalOrderSourceFields,
} from './portalOrderSource.js'

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const ACCEPTED_EXT = /\.(pdf|jpe?g|png)$/i

/**
 * @param {File} file
 */
export function validatePoAttachmentFile(file) {
  if (!file) return 'Please select a PO document.'
  if (file.size > 15 * 1024 * 1024) return 'File must be 15 MB or smaller.'
  const typeOk = ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXT.test(file.name)
  if (!typeOk) return 'Only PDF, JPG, or PNG files are accepted.'
  return null
}

/**
 * @param {Record<string, unknown>} row
 */
export function mapPoAttachmentFromApi(row) {
  const id = row.id ?? row._id ?? row.attachmentId
  return {
    id,
    name: String(row.fileName ?? row.name ?? 'PO attachment'),
    fileUrl: row.fileUrl ?? row.downloadUrl ?? null,
    mimeType: row.mimeType ?? row.contentType ?? null,
    uploadedAt: row.uploadedAt ?? row.createdAt ?? null,
    pending: false,
    localFile: null,
  }
}

/**
 * PO files belong to a single invoice, not the org.
 * @param {Record<string, unknown>} doc
 */
export function poAttachmentsFromDocument(doc) {
  if (!Array.isArray(doc?.poAttachments)) return []
  return doc.poAttachments.map((f, i) =>
    mapPoAttachmentFromApi({ ...f, id: f?.id ?? `po-${i}` })
  )
}

/** @param {string | null | undefined} poNumber */
export function hasPoNumber(poNumber) {
  return Boolean(String(poNumber ?? '').trim())
}

/** @param {Array<Record<string, unknown>> | null | undefined} attachments */
export function hasPersistedPoAttachments(attachments) {
  return serializePoAttachmentsForApi(attachments).length > 0
}

/** Includes local pending picks (UI “pair started” only). */
export function hasPoAttachments(attachments) {
  return (
    Array.isArray(attachments) &&
    attachments.some(
      (a) =>
        (a.pending && a.localFile instanceof File) ||
        (!a.pending && a.id != null && !String(a.id).startsWith('pending-'))
    )
  )
}

/**
 * User started PO section (entered a number and/or picked a file).
 * @param {string | null | undefined} poNumber
 * @param {Array<Record<string, unknown>> | null | undefined} attachments
 */
export function isPoPairStarted(poNumber, attachments) {
  return hasPoNumber(poNumber) || hasPoAttachments(attachments)
}

/**
 * PO number and PO document are optional together, but required as a pair.
 * Either both are provided or neither — one alone is not sent to the API.
 * @param {string | null | undefined} poNumber
 * @param {Array<Record<string, unknown>> | null | undefined} attachments
 */
export function validatePoNumberAndAttachments(poNumber, attachments) {
  const hasNumber = hasPoNumber(poNumber)
  const hasFiles = hasPoAttachments(attachments)
  if (!hasNumber && !hasFiles) {
    return ''
  }
  if (!hasNumber) {
    return 'Enter a PO number to go with the attached document.'
  }
  if (!hasFiles) {
    return 'Attach a PO document (PDF or image) to go with the PO number.'
  }
  return ''
}

/**
 * JSON billing APIs: send PO only when number and at least one persisted file both exist.
 * @param {string | null | undefined} poNumber
 * @param {Array<Record<string, unknown>> | null | undefined} attachments
 */
export function resolvePoPayloadForApi(poNumber, attachments) {
  const poAttachments = serializePoAttachmentsForApi(attachments)
  const trimmed = String(poNumber ?? '').trim()
  if (poAttachments.length > 0 && trimmed) {
    return { poNumber: trimmed, poAttachments }
  }
  return { poNumber: '', poAttachments: [] }
}

/**
 * Denied or pending PO must not be re-sent until the user enters a new pair.
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function isPoReviewLockedForResubmit(doc) {
  const status = String(doc?.poReviewStatus ?? '').toLowerCase()
  return status === 'denied' || status === 'deny' || status === 'pending'
}

/**
 * Editable PO form state when opening or refreshing a billing document.
 * Clears inputs when review is locked so address-only saves do not re-submit PO.
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function resolveEditablePoFromDocument(doc) {
  if (!doc || doc.documentType === 'quote') {
    return { poNumber: '', poAttachments: [] }
  }
  if (isPoReviewLockedForResubmit(doc)) {
    return { poNumber: '', poAttachments: [] }
  }
  return {
    poNumber: String(doc.poNumber ?? '').trim(),
    poAttachments: poAttachmentsFromDocument(doc),
  }
}

/**
 * Read-only PO display from the persisted document (for denied/pending banners and view mode).
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function resolveDocumentPoDisplay(doc) {
  if (!doc || doc.documentType === 'quote') {
    return { poNumber: '', attachments: [] }
  }
  return {
    poNumber: String(doc.poNumber ?? '').trim(),
    attachments: poAttachmentsFromDocument(doc),
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function documentHasPoContent(doc) {
  if (!doc) return false
  return (
    hasPoNumber(doc.poNumber) ||
    (Array.isArray(doc.poAttachments) && doc.poAttachments.length > 0)
  )
}

/**
 * PO review status does not gate invoice generation.
 * @param {Record<string, unknown> | null | undefined} _doc
 */
export function isPoBlockingInvoice(_doc) {
  return false
}

/**
 * @param {Record<string, unknown> | null | undefined} _doc
 */
export function poReviewBlockedMessage(_doc) {
  return ''
}

/**
 * Only persisted attachments are sent on JSON billing APIs.
 * @param {Array<Record<string, unknown>> | null | undefined} attachments
 */
export function serializePoAttachmentsForApi(attachments) {
  return (attachments || [])
    .filter((f) => !f.pending && f.id != null && !String(f.id).startsWith('pending-'))
    .map((f) => ({
      id: f.id,
      name: f.name,
    }))
}

/**
 * @param {Record<string, unknown> | null | undefined} att
 * @param {File | null | undefined} localFile
 */
export function poAttachmentPreviewSource(att, localFile = null) {
  const file = localFile ?? (att?.localFile instanceof File ? att.localFile : null)
  if (file) {
    return taxExemptCertPreviewSource(null, file)
  }
  return taxExemptCertPreviewSource(
    {
      fileUrl: att?.fileUrl,
      name: att?.name,
      mimeType: att?.mimeType,
    },
    null
  )
}

/**
 * @param {Record<string, unknown>} data
 * @param {Array<Record<string, unknown>>} [existing]
 */
export function mergePoAttachmentsFromResponse(data, existing = []) {
  const rows =
    data?.attachment ??
    data?.attachments ??
    data?.poAttachments ??
    data?.poAttachment
  if (!rows) return null
  const mapped = (Array.isArray(rows) ? rows : [rows]).map(mapPoAttachmentFromApi)
  const byId = new Map(existing.map((a) => [String(a.id), a]))
  mapped.forEach((a) => byId.set(String(a.id), a))
  return [...byId.values()]
}

/**
 * Upload a PO document for one invoice.
 * @param {{
 *   organizationId: string,
 *   documentId: string | number,
 *   file: File,
 *   poNumber?: string,
 *   uploadedBy?: { userId?: string, name?: string, email?: string },
 * }} payload
 */
export async function uploadBillingDocumentPoAttachment(payload) {
  const { organizationId, documentId, file, poNumber, uploadedBy } = payload
  const err = validatePoAttachmentFile(file)
  if (err) throw new Error(err)

  const trimmedPo = String(poNumber ?? '').trim()
  if (!trimmedPo) {
    throw new Error('Enter a PO number to go with the attached document.')
  }

  const form = new FormData()
  form.append('file', file)
  form.append('organizationId', organizationId)
  form.append('documentId', String(documentId))
  form.append('poNumber', trimmedPo)
  form.append('uploadedBy', JSON.stringify(uploadedBy ?? {}))
  appendInstructorPortalOrderSource(form)

  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/upload-billing-document-po-attachment`,
    form,
    {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return data
}

/**
 * @param {{
 *   organizationId: string,
 *   documentId: string | number,
 *   attachmentId: string | number,
 * }} payload
 */
export async function removeBillingDocumentPoAttachment(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/remove-billing-document-po-attachment`,
    { ...payload, ...instructorPortalOrderSourceFields() },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data
}
